/**
 *
 * FormDateTimePicker
 *
 */
import { isValid } from 'date-fns';
import { FormikProps, FieldInputProps } from 'formik';
import * as React from 'react';
import { DateTimeControl } from '@assetic/web-components';
import { convertToLocalDateStr, justADate } from 'app/helpers/date-helper';

export interface FormDateTimePickerProps {
  form: FormikProps<any>;
  field: FieldInputProps<any>;
  generateHelperText?: (date: Date) => React.ReactNode;
  minDate?: Date;
  label: string;
}

interface CreateHelperTextArgs {
  errorText: string | undefined;
  generateHelperText?: (date: Date) => React.ReactNode;
  value: Date;
}

const createHelperText = (args: CreateHelperTextArgs): React.ReactNode => {
  const { errorText, generateHelperText, value } = args;
  if (errorText) return errorText;
  if (generateHelperText) return generateHelperText(value);
  return undefined;
};

// error message will come from yup form validation through errors
export const FormDateTimePicker = (props: FormDateTimePickerProps) => {
  const {
    field: { value, name },
    form: { setFieldValue, errors },
    label,
    generateHelperText,
    minDate,
    ...rest
  } = props;
  const formikError = errors[name];
  const isError = Boolean(formikError);
  const errorText = isError ? (formikError as string) : undefined;
  const helperText = createHelperText({
    errorText,
    generateHelperText,
    value: new Date(value),
  });

  const onChange = (date: Date | undefined) => {
    const dateValue =
      date && isValid(date) ? justADate(date).toISOString() : date;
    setFieldValue(name, dateValue, true);
  };

  const pickerValue =
    value && typeof value === 'string' ? convertToLocalDateStr(value) : value;

  return (
    <>
      <DateTimeControl
        clearable
        id={name}
        name={name}
        label={label}
        type="date"
        format="yyyy/MM/dd"
        value={pickerValue}
        onChange={onChange}
        minDateTime={minDate ?? new Date('1970-01-01T00:00:00')}
        error={isError}
        helperText={helperText}
        {...rest}
      />
    </>
  );
};
